import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';
import { Wrench, Moon, Sun, Eye, EyeOff, Loader2, ShieldAlert, Lock, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Identidad confirmada. Accediendo...');
      navigate('/admin');
    } catch (error) {
      toast.error('Error de autenticación: Acceso denegado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] flex flex-col relative overflow-hidden selection:bg-primary selection:text-white">
      
      {/* 1. EFECTO DE LUZ DE SEGURIDAD */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-primary/5 blur-[120px] rounded-full pointer-events-none"></div>

      {/* Header */}
      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/20 transition-transform group-hover:scale-105">
                <Wrench className="w-5 h-5 text-white" />
              </div>
              <span className="font-black italic text-xl tracking-tighter text-white uppercase">TechFix <span className="text-primary">Admin</span></span>
            </Link>
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full hover:bg-white/5 text-white">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </nav>

      {/* Login Form Section */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-[420px] animate-in fade-in slide-in-from-bottom-4 duration-700">
          
          <div className="bg-[#0A0A0A] border border-white/10 rounded-[2.5rem] p-10 shadow-2xl relative overflow-hidden">
            
            {/* Badge de Seguridad Cerrada */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-[10px] font-black uppercase tracking-widest text-red-500">
                <ShieldAlert className="w-3 h-3" /> Sistema Privado
              </div>
            </div>

            <div className="text-center mb-10">
              <h2 className="text-3xl font-black italic tracking-tighter text-white uppercase leading-none">Autenticación</h2>
              <p className="text-slate-600 font-bold text-[10px] uppercase tracking-[0.2em] mt-3 italic">Solo personal autorizado — Aurora, IL Hub</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Email de Usuario</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="usuario@techfix.pro"
                  className="bg-white/[0.03] border-white/10 h-14 pl-4 rounded-xl text-white font-bold italic focus:border-primary/50 transition-all placeholder:text-slate-800"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Password</Label>
                <div className="relative group">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="bg-white/[0.03] border-white/10 h-14 pl-4 pr-12 rounded-xl text-white font-bold italic focus:border-primary/50 transition-all placeholder:text-slate-800"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-600 hover:text-primary transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-16 rounded-2xl bg-primary hover:bg-primary/90 text-white font-black italic text-lg shadow-[0_10px_30px_rgba(var(--primary),0.2)] transition-all active:scale-95 group" 
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">
                    CONFIRMAR IDENTIDAD <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                )}
              </Button>
            </form>

            {/* Footer de la tarjeta: Eliminado el link de registro */}
            <div className="mt-8 pt-6 border-t border-white/5 text-center">
              <p className="text-[9px] text-slate-700 font-black uppercase tracking-[0.3em]">
                Encriptación End-to-End Activa
              </p>
            </div>
          </div>

          <div className="mt-8 text-center">
             <Link to="/" className="text-[10px] font-black text-slate-600 uppercase tracking-widest hover:text-primary transition-colors italic">
               ← Volver al Portal Público
             </Link>
          </div>
        </div>
      </div>
    </div>
  );
}